import React, { useState } from "react";
import * as XLSX from "xlsx";
import { customRequestService } from "../../services";
import * as repos from "../../mock/repositories";
import { EXPORT_DATASETS, filterByRange, flattenRow, onlyOrg, validateRange } from "../../config/export";
import { API_ACCESS_STATUS } from "../../config/custom-request";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, DataTable, Input, PageHead, Textarea, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function ExportData() {
  const { orgId, organization, hasModule, profile, toast } = useApp();
  const { tx, pick } = useI18n();
  const by = profile?.fullname || profile?.email || "";

  const available = EXPORT_DATASETS.filter((item) => hasModule(item.module));
  const [selected, setSelected] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const [note, setNote] = useState("");
  const [contact, setContact] = useState("");
  const [sending, setSending] = useState(false);
  const { data: apiRequests, reload: reloadApi } = useAsync(() => customRequestService.listApi(orgId), [orgId], []);
  const openRequest = (apiRequests || []).find((row) => row.status === "pending_quote" || row.status === "in_progress");

  const toggle = (key) => setSelected((rows) => (rows.includes(key) ? rows.filter((row) => row !== key) : [...rows, key]));

  const exportExcel = async () => {
    const rangeError = validateRange(from, to);
    if (rangeError) return toast(rangeError, "error");
    const picked = available.filter((item) => selected.includes(item.key));
    if (!picked.length) return toast(tx("เลือกข้อมูลที่จะ Export อย่างน้อย 1 รายการ", "Select at least one dataset"), "error");

    setBusy(true);
    try {
      const book = XLSX.utils.book_new();
      let total = 0;
      for (const item of picked) {
        // ดึงเฉพาะข้อมูลของร้านตัวเอง (repo scope ด้วย orgId + กรอง org_id ซ้ำอีกชั้น)
        const rows = filterByRange(onlyOrg(await repos[item.repo].all(orgId), orgId), item.dateField, from, to).map(flattenRow);
        total += rows.length;
        const sheet = rows.length ? XLSX.utils.json_to_sheet(rows) : XLSX.utils.aoa_to_sheet([[tx("ไม่มีข้อมูลในช่วงที่เลือก", "No data in the selected range")]]);
        XLSX.utils.book_append_sheet(book, sheet, item.nameEn.replace(/[\\/?*[\]:]/g, " ").slice(0, 31));
      }
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(book, `${(organization?.code || "shop").toLowerCase()}-export-${stamp}.xlsx`);
      toast(tx(`Export สำเร็จ ${total.toLocaleString()} แถว`, `Exported ${total.toLocaleString()} rows`), "success");
    } catch (err) {
      toast(err.message || tx("Export ไม่สำเร็จ", "Export failed"), "error");
    } finally {
      setBusy(false);
    }
  };

  const requestApi = async () => {
    setSending(true);
    try {
      await customRequestService.requestApiAccess({ orgId, note, contact, by });
      setNote("");
      setContact("");
      reloadApi();
      toast(tx("บันทึกคำขอแล้ว ทีมงานจะติดต่อกลับ", "Request saved — our team will contact you"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSending(false);
    }
  };

  const cancelApi = async (row) => {
    try {
      await customRequestService.cancel(orgId, row.id);
      reloadApi();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <>
      <PageHead title={tx("Export ข้อมูล & API", "Data export & API")} subtitle={tx("ดาวน์โหลดข้อมูลร้านของคุณเป็น Excel และขอเปิดใช้ API", "Download your shop's data as Excel and request API access")} />

      <div className="grid-2" style={{ alignItems: "start" }}>
        <Card title={tx("Export Excel", "Export to Excel")} subtitle={tx("เฉพาะข้อมูลของร้านคุณเท่านั้น · เลือกได้หลายชุด แยกเป็นชีตในไฟล์เดียว", "Your shop's data only · pick several datasets, one sheet each")}>
          <div style={{ display: "grid", gap: 6 }} className="u-mb-16">
            {available.map((item) => (
              <label key={item.key} className="u-fs-13" style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={selected.includes(item.key)} onChange={() => toggle(item.key)} />
                {pick(item)}
              </label>
            ))}
          </div>
          <div className="u-between u-mb-8" style={{ gap: 8 }}>
            <Button size="sm" onClick={() => setSelected(available.map((item) => item.key))}>{tx("เลือกทั้งหมด", "Select all")}</Button>
            <Button size="sm" onClick={() => setSelected([])}>{tx("ล้าง", "Clear")}</Button>
          </div>
          <div className="grid-2">
            <Input label={tx("ตั้งแต่วันที่", "From")} type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            <Input label={tx("ถึงวันที่", "To")} type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </div>
          <div className="u-fs-12 u-muted u-mb-8">
            {tx("ไม่ระบุวันที่ = ส่งออกทั้งหมด · กรองด้วยวันที่สร้าง (นัดหมายใช้วันเวลานัด)", "No dates = everything · filtered by created date (appointments by appointment time)")}
          </div>
          <Button variant="primary" block disabled={busy} onClick={exportExcel}>
            <Icon name="download" /> {busy ? tx("กำลังสร้างไฟล์...", "Building file...") : tx("ดาวน์โหลด Excel", "Download Excel")}
          </Button>
        </Card>

        <Card title={tx("API", "API")} subtitle={tx("ขอเปิดใช้ API เพื่อเชื่อมต่อระบบของคุณ", "Request API access to connect your own systems")}>
          <div className="card-crm u-mb-16" style={{ marginBottom: 12, background: "#fff7ed", borderColor: "#fdba74" }}>
            <div className="u-fs-13">
              {tx(
                "ตอนนี้ระบบยังไม่ออก API key อัตโนมัติ — เมื่อกดขอเปิดใช้ ทีมงานจะติดต่อกลับพร้อม credential (API key) ให้ตามช่องทางที่คุณระบุ",
                "API keys are not issued automatically yet. After you request access, our team will contact you with the credentials via the contact you provide."
              )}
            </div>
          </div>
          {openRequest ? (
            <div className="u-fs-13">
              <Badge tone={(API_ACCESS_STATUS[openRequest.status] || {}).tone}>{tx(API_ACCESS_STATUS[openRequest.status]?.th || openRequest.status, API_ACCESS_STATUS[openRequest.status]?.en || openRequest.status)}</Badge>{" "}
              {tx(`คุณส่งคำขอเมื่อ ${formatDate(openRequest.createdAt, true)} (${openRequest.requestNo})`, `Requested on ${formatDate(openRequest.createdAt, true)} (${openRequest.requestNo})`)}
            </div>
          ) : (
            <>
              <Input label={tx("ช่องทางติดต่อกลับ (อีเมล/เบอร์/LINE)", "Contact (email / phone / LINE)")} value={contact} onChange={(event) => setContact(event.target.value)} />
              <Textarea label={tx("ใช้ API ทำอะไร (ถ้ามี)", "What will you use it for (optional)")} rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
              <Button variant="primary" block disabled={sending || !contact.trim()} onClick={requestApi}>
                {tx("ขอเปิดใช้ API", "Request API access")}
              </Button>
            </>
          )}
        </Card>
      </div>

      <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>{tx("คำขอเปิดใช้ API", "API requests")}</div>
      <DataTable
        rows={apiRequests || []}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "requestNo", label: tx("เลขที่", "No.") },
          { key: "contact", label: tx("ช่องทางติดต่อ", "Contact") },
          {
            key: "status",
            label: tx("สถานะ", "Status"),
            render: (row) => {
              const status = API_ACCESS_STATUS[row.status] || { th: row.status, en: row.status, tone: "gray" };
              return <Badge tone={status.tone}>{tx(status.th, status.en)}</Badge>;
            },
          },
          {
            key: "action",
            label: "",
            align: "right",
            render: (row) => (row.status === "pending_quote" ? <Button size="sm" onClick={() => cancelApi(row)}>{tx("ยกเลิก", "Cancel")}</Button> : null),
          },
        ]}
        emptyText={tx("ยังไม่มีคำขอ", "No requests yet")}
      />
    </>
  );
}
